/**
 * Billing Utility Functions
 *
 * Utility functions for formatting, calculating, and validating billing data.
 */

import { InvoiceTotals } from './helper-types';
import { InvoiceType, SupplyType, InvoiceStatus, PaymentStatus, InvoiceItem } from './invoice-types';
import { PartyType } from './party-types';
import { getCurrencyConfig, toSmallestUnit, fromSmallestUnit } from '../../utils/formatters';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format amount from smallest currency unit to display string.
 * Uses currency config for correct decimal places and locale.
 */
export function formatAmount(
  amount: number,
  currency: string = 'INR'
): string {
  const config = getCurrencyConfig(currency);
  const decimal = fromSmallestUnit(amount, currency);

  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: config.decimalPlaces,
    maximumFractionDigits: config.decimalPlaces,
  }).format(decimal);
}

/**
 * Convert decimal amount to smallest currency unit.
 */
export function toPaisa(amount: number, currency: string = 'INR'): number {
  return toSmallestUnit(amount, currency);
}

/**
 * Convert smallest currency unit to decimal amount.
 */
export function toRupees(amount: number, currency: string = 'INR'): number {
  return fromSmallestUnit(amount, currency);
}

/**
 * Calculate GST amounts based on supply type.
 */
export function calculateGST(
  taxableAmount: number,
  gstRate: number,
  supplyType: SupplyType
): { cgst: number; sgst: number; igst: number } {
  if (supplyType === 'export') {
    return { cgst: 0, sgst: 0, igst: 0 };
  }

  if (supplyType === 'interstate' || supplyType === 'sez') {
    return {
      cgst: 0,
      sgst: 0,
      igst: Math.round(taxableAmount * gstRate / 100),
    };
  }

  // Intrastate
  const halfRate = gstRate / 2;
  return {
    cgst: Math.round(taxableAmount * halfRate / 100),
    sgst: Math.round(taxableAmount * halfRate / 100),
    igst: 0,
  };
}

/**
 * Determine supply type based on seller and buyer state codes.
 */
export function determineSupplyType(
  sellerStateCode: string,
  buyerStateCode: string | undefined,
  isExport: boolean
): SupplyType {
  if (isExport || !buyerStateCode) {
    return 'export';
  }
  return sellerStateCode === buyerStateCode ? 'intrastate' : 'interstate';
}

/**
 * Get invoice type display name.
 */
export function getInvoiceTypeLabel(type: InvoiceType): string {
  const labels: Record<InvoiceType, string> = {
    b2b: 'B2B Tax Invoice',
    export: 'Export Invoice',
    b2c: 'B2C Invoice',
     
    credit_note: 'Credit Note',
     
    debit_note: 'Debit Note',
     
    vendor_bill: 'Vendor Bill',
     
    vendor_credit_note: 'Vendor Credit Note',
  };
  return labels[type] || type;
}

/**
 * Get party type display name.
 */
export function getPartyTypeLabel(type: PartyType): string {
  const labels: Record<PartyType, string> = {
    customer: 'Customer',
    vendor: 'Vendor',
    both: 'Customer & Vendor',
  };
  return labels[type] || type;
}

/**
 * Get invoice status display name.
 */
export function getInvoiceStatusLabel(status: InvoiceStatus): string {
  const labels: Record<InvoiceStatus, string> = {
    draft: 'Draft',
    posted: 'Posted',
    voided: 'Voided',
    cancelled: 'Cancelled',
  };
  return labels[status] || status;
}

/**
 * Get payment status display name.
 */
export function getPaymentStatusLabel(status: PaymentStatus): string {
  const labels: Record<PaymentStatus, string> = {
    unpaid: 'Unpaid',
    partial: 'Partially Paid',
    paid: 'Paid',
    overpaid: 'Overpaid',
    written_off: 'Written Off',
  };
  return labels[status] || status;
}

/**
 * Validate GSTIN format.
 */
export function isValidGSTIN(gstin: string): boolean {
  if (!gstin) {return true;} // Empty is valid (for optional fields)
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  return gstinRegex.test(gstin);
}

/**
 * Extract state code from GSTIN.
 */
export function getStateCodeFromGSTIN(gstin: string): string | undefined {
  if (!gstin || gstin.length < 2) {return undefined;}
  return gstin.substring(0, 2);
}

/**
 * Calculate invoice totals from items.
 */
export function calculateInvoiceTotals(items: InvoiceItem[]): InvoiceTotals {
  return items.reduce(
    (totals, item) => ({
      subtotal: totals.subtotal + item.grossAmount,
      discountAmount: totals.discountAmount + item.discountAmount,
      taxableAmount: totals.taxableAmount + item.taxableAmount,
      cgstAmount: totals.cgstAmount + item.cgstAmount,
      sgstAmount: totals.sgstAmount + item.sgstAmount,
      igstAmount: totals.igstAmount + item.igstAmount,
      cessAmount: totals.cessAmount + item.cessAmount,
      totalTax: totals.totalTax + item.totalTax,
      totalAmount: totals.totalAmount + item.totalAmount,
    }),
    {
      subtotal: 0,
      discountAmount: 0,
      taxableAmount: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      cessAmount: 0,
      totalTax: 0,
      totalAmount: 0,
    }
  );
}
